import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Download, Copy, Check, Image as ImageIcon, FileText, Mail,
  Palette, Type, Sparkles, Instagram, Facebook, Linkedin, Award, Globe,
  CreditCard, Newspaper, Printer, Info, DoorOpen, BookOpen, GraduationCap, Loader2,
} from "lucide-react";
import {
  BRAND_COLORS, LOGO_SVG_VECTOR, LOGO_SVG_WHITE, TEMPLATES, MARKETING_TEXTS,
  EMAIL_SIGNATURE_HTML, EMAIL_SIGNATURE_TEXT,
  BUSINESS_CARD_TEMPLATES, FLYER_TEMPLATES,
  TUERSCHILD_TEMPLATES, DINLANG_TEMPLATES,
  easyGutachterEmail,
} from "@/lib/brandTemplates";
import {
  downloadFromUrl, downloadSvgString, svgStringToPngDownload, copyToClipboard,
} from "@/lib/downloadHelpers";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/lib/useSubscription";
import FeatureLockedScreen from "@/components/FeatureLockedScreen";
import { ShopWerbungTabs } from "@/components/ShopWerbungTabs";

export default function Werbematerial() {
  const { isActive, isAdmin } = useSubscription();
  if (!isActive && !isAdmin) {
    return (
      <Layout title="Werbematerial">
        <div className="mb-5"><ShopWerbungTabs /></div>
        <FeatureLockedScreen
          icon={Megaphone}
          title="Werbematerial wird mit deinem Abo freigeschaltet"
          subtitle="Erstelle in Sekunden druckfertige Visitenkarten, Flyer, Türschilder und E-Mail-Signaturen — automatisch mit deinem Profil befüllt."
          bullets={[
            "Visitenkarten 85×55 mm, 3 mm Beschnitt, 300 DPI",
            "Flyer A6 / DIN-lang Wickelfalz, sofort druckfertig",
            "Türschilder & Briefbogen mit deinem Logo",
            "E-Mail-Signaturen mit deinem Easy-Gutachter-Mailbox",
            "Direkt zum Druckdienstleister oder als PDF-Download",
            "Automatisches Auto-Fill aus deinem Profil",
          ]}
          trigger="werbematerial"
        />
      </Layout>
    );
  }
  return <WerbematerialInner />;
}

function WerbematerialInner() {  const [profile, setProfile] = useState(null);
  // Shared placeholders for template generation
  const [name, setName] = useState("Max Mustermann");
  const [headline, setHeadline] = useState("Schadensgutachten");
  const [sub, setSub] = useState("in 24 Stunden");

  // Email signature placeholders (also auto-filled from profile)
  const [sigPhone, setSigPhone] = useState("+49 30 1234567");
  const [sigEmail, setSigEmail] = useState("info@example.de");
  const [sigAddress, setSigAddress] = useState("");

  // Print-Materialien (Visitenkarte + Flyer + Türschild + DIN-lang)
  const [printRole, setPrintRole] = useState("Kfz-Sachverständiger");
  const [printMobile, setPrintMobile] = useState("");
  const [printWeb, setPrintWeb] = useState("www.easygutachter.de");
  const [printHours, setPrintHours] = useState("Mo-Fr  08:00 - 18:00\nSa  nach Vereinbarung");

  // Visitenkarte: nur lokaler Teil der Easy-Gutachter-Mail (Domain ist fix)
  const [bcEmailLocal, setBcEmailLocal] = useState("");
  // Visitenkarte: nur Slug-Teil der Easy-Gutachter-URL (Domain easygutachter.de/ ist fix)
  const [bcWebSlug, setBcWebSlug] = useState("");

  // Schulungs-Downloads (PPTX + PDF-Handout)
  const [pptxBusy, setPptxBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadTraining = async (path, filename, setBusy) => {
    setBusy(true);
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${filename} heruntergeladen`);
    } catch (e) {
      toast.error("Download fehlgeschlagen — bitte erneut versuchen");
    } finally {
      setBusy(false);
    }
  };
  const downloadTrainingPptx = () =>
    downloadTraining("/training/schulung.pptx", "Easy-Gutachter-Schulung.pptx", setPptxBusy);
  const downloadTrainingPdf = () =>
    downloadTraining("/training/schulung.pdf", "Easy-Gutachter-Schulung-Handout.pdf", setPdfBusy);

  useEffect(() => {
    api.get("/users/me/profile").then(({ data }) => {
      const u = data.user || {};
      setProfile(u);
      if (u.name) {
        setName(u.name);
        // Visitenkarte-Mail aus dem Namen ableiten (vorname.nachname)
        const fullEasy = easyGutachterEmail(u.name);
        const local = fullEasy ? fullEasy.replace(/@easygutachter\.de$/, "") : "";
        if (local) setBcEmailLocal(local);
        // Visitenkarte-Web-Slug: Bindestrich-Form (vorname-nachname)
        const slug = String(u.name)
          .toLowerCase()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/ß/g, "ss")
          .replace(/[^a-z0-9\s]/g, "")
          .trim()
          .split(/\s+/)
          .join("-");
        if (slug) setBcWebSlug(slug);
      }
      if (u.email) setSigEmail(u.email);
      if (u.mobile || u.phone) setSigPhone(u.mobile || u.phone);
      if (u.mobile) setPrintMobile(u.mobile);
      const addr = [u.address_line, [u.postal_code, u.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      if (addr) setSigAddress(addr);
    }).catch(() => { /* ignore */ });
  }, []);

  return (
    <Layout
      title="Werbematerial"
      subtitle="Logos, Vorlagen, Texte und alles für deinen Auftritt als Easy Gutachter Partner"
    >
      <div className="max-w-6xl space-y-8">
        <ShopWerbungTabs />
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-cyan-500 to-emerald-500 text-white p-7" data-testid="werbe-hero">
          <div className="absolute right-6 top-6 opacity-30"><Megaphone className="h-24 w-24" /></div>
          <Badge className="bg-white/20 backdrop-blur border-white/30 text-white mb-3">
            <Sparkles className="h-3 w-3 mr-1" /> Brand Kit · Social Media · E-Mail · Texte
          </Badge>
          <h2 className="font-display text-3xl font-bold tracking-tight max-w-2xl">
            Dein komplettes Marketing-Toolkit.
          </h2>
          <p className="mt-2 text-white/90 max-w-xl">
            Logos, Brand-Farben, Social-Media-Vorlagen, E-Mail-Signaturen und vorgeschriebene Texte —
            alles passend zu deinem Easy Gutachter Auftritt. Einfach anpassen und herunterladen.
          </p>
        </div>

        {/* Tabbed sections */}
        <Tabs defaultValue="logo" className="w-full">
          <TabsList className="flex flex-wrap h-auto bg-zinc-100 p-1 rounded-xl mb-6">
            <TabsTrigger value="logo" data-testid="tab-logo" className="flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> Logo & Brand</TabsTrigger>
            <TabsTrigger value="visitenkarte" data-testid="tab-visitenkarte" className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Visitenkarte</TabsTrigger>
            <TabsTrigger value="flyer" data-testid="tab-flyer" className="flex items-center gap-1.5"><Newspaper className="h-3.5 w-3.5" /> Flyer A6</TabsTrigger>
            <TabsTrigger value="dinlang" data-testid="tab-dinlang" className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Falt-Flyer</TabsTrigger>
            <TabsTrigger value="tuerschild" data-testid="tab-tuerschild" className="flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" /> Türschild</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social" className="flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5" /> Social Media</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> E-Mail-Signatur</TabsTrigger>
            <TabsTrigger value="texts" data-testid="tab-texts" className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Marketing-Texte</TabsTrigger>
            <TabsTrigger value="web" data-testid="tab-web" className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Web-Badges</TabsTrigger>
            <TabsTrigger value="schulung" data-testid="tab-schulung" className="flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> Schulung</TabsTrigger>
          </TabsList>

          {/* ========= LOGO & BRAND ========= */}
          <TabsContent value="logo" className="space-y-6">
            <SectionHeader icon={Award} title="Logo-Paket" description="Vier Varianten — original (Farbe), Vektor (SVG), weiß für dunkle Hintergründe und Favicon." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="logo-grid">
              <LogoCard
                bg="bg-white"
                label="Logo · Original"
                hint="PNG · transparenter Hintergrund"
                onDownload={() => downloadFromUrl("/easy-gutachter-logo.png", "easy-gutachter-logo.png")}
                testid="logo-png"
              >
                <img src="/easy-gutachter-logo.png" alt="Logo Original" className="max-h-32 object-contain" />
              </LogoCard>

              <LogoCard
                bg="bg-white"
                label="Logo · Vektor"
                hint="SVG · skaliert verlustfrei"
                onDownload={() => downloadSvgString(LOGO_SVG_VECTOR, "easy-gutachter-logo.svg")}
                testid="logo-svg"
              >
                <div className="max-h-32" dangerouslySetInnerHTML={{ __html: LOGO_SVG_VECTOR }} style={{ height: 128, width: 128 }} />
              </LogoCard>

              <LogoCard
                bg="bg-zinc-900"
                label="Logo · Weiß"
                hint="SVG für dunkle Hintergründe"
                onDownload={() => downloadSvgString(LOGO_SVG_WHITE, "easy-gutachter-logo-white.svg")}
                onDownloadPng={() => svgStringToPngDownload(LOGO_SVG_WHITE, "easy-gutachter-logo-white.png", { width: 1024, height: 1024, background: "#0F172A" })}
                pngLabel="PNG (Dunkler BG)"
                testid="logo-white"
              >
                <div className="max-h-32" style={{ height: 128, width: 128 }} dangerouslySetInnerHTML={{ __html: LOGO_SVG_WHITE }} />
              </LogoCard>

              <LogoCard
                bg="bg-gradient-to-br from-cyan-500 to-emerald-500"
                label="Favicon"
                hint="ICO/PNG · 64×64 px"
                onDownload={() => svgStringToPngDownload(LOGO_SVG_VECTOR, "easy-gutachter-favicon.png", { width: 256, height: 256, background: null })}
                pngLabel="Download PNG"
                testid="logo-favicon"
              >
                <img src="/easy-gutachter-logo.png" alt="Favicon" className="h-16 w-16 rounded-2xl bg-white p-2 shadow-md" />
              </LogoCard>
            </div>

            {/* Brand colors */}
            <SectionHeader icon={Palette} title="Brand-Farben" description="Klick auf einen Hex-Code zum Kopieren in die Zwischenablage." />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="brand-colors">
              {BRAND_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => copyToClipboard(c.hex, `${c.name} (${c.hex}) kopiert`)}
                  className="rounded-xl border border-zinc-200 bg-white overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all text-left"
                  data-testid={`brand-color-${c.name.toLowerCase()}`}
                >
                  <div className={cn("aspect-[3/2]", c.className)} />
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">{c.role}</div>
                    <div className="font-display font-semibold tracking-tight">{c.name}</div>
                    <div className="font-mono text-xs text-zinc-500 mt-0.5">{c.hex}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Fonts */}
            <SectionHeader icon={Type} title="Schriften" description="Frei verfügbar via Google Fonts — kommerziell nutzbar." />
            <div className="grid sm:grid-cols-2 gap-4">
              <FontCard
                role="Headlines"
                family="Outfit"
                preview="Easy Gutachter"
                sample="Aa Bb Cc 1234"
                href="https://fonts.google.com/specimen/Outfit"
                style={{ fontFamily: "Outfit, -apple-system, system-ui, sans-serif", fontWeight: 700 }}
              />
              <FontCard
                role="Fließtext"
                family="Outfit"
                preview="Schadensgutachten in 24 h."
                sample="Aa Bb Cc 1234"
                href="https://fonts.google.com/specimen/Outfit"
                style={{ fontFamily: "Outfit, -apple-system, system-ui, sans-serif", fontWeight: 500 }}
              />
            </div>
          </TabsContent>

          {/* ========= VISITENKARTE ========= */}
          <TabsContent value="visitenkarte" className="space-y-6">
            <SectionHeader
              icon={CreditCard}
              title="Visitenkarte"
              description="Vorder- und Rückseite im Standardformat 85 × 55 mm — druckfertig in 300 DPI. Daten werden aus deinem Profil übernommen, lassen sich aber jederzeit überschreiben."
            />

            <Card className="p-5" data-testid="visitenkarte-fields">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name" value={name} onChange={setName} testid="bc-name" />
                <Field label="Rolle / Titel" value={printRole} onChange={setPrintRole} testid="bc-role" />
                <Field label="Telefon" value={sigPhone} onChange={setSigPhone} testid="bc-phone" />
                <Field label="Mobil" value={printMobile} onChange={setPrintMobile} testid="bc-mobile" />
                <EasyGutachterEmailField
                  value={bcEmailLocal}
                  onChange={setBcEmailLocal}
                  testid="bc-email-local"
                />
                <EasyGutachterWebField
                  value={bcWebSlug}
                  onChange={setBcWebSlug}
                  testid="bc-web-slug"
                />
                <div className="sm:col-span-2">
                  <Field label="Adresse" value={sigAddress} onChange={setSigAddress} testid="bc-address" />
                </div>
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-4" data-testid="visitenkarte-grid">
              {BUSINESS_CARD_TEMPLATES.map((t) => (
                <PrintTemplateCard
                  key={t.id}
                  template={t}
                  data={{
                    name, role: printRole,
                    phone: sigPhone, mobile: printMobile,
                    email: bcEmailLocal ? `${bcEmailLocal}@easygutachter.de` : "",
                    web: bcWebSlug ? `www.easygutachter.de/${bcWebSlug}` : "www.easygutachter.de",
                    address: sigAddress,
                  }}
                  filenamePrefix={`visitenkarte-${t.id}`}
                  hint="91 × 61 mm · 300 DPI · druckfertig (inkl. 3 mm Beschnitt)"
                />
              ))}
            </div>

            <PrintHintCard
              format="Endformat 85 × 55 mm · Druckformat 91 × 61 mm (mit 3 mm Beschnitt rundum)"
              dpi="300 DPI · 1076 × 722 px · druckfertig"
              tip={`Datei direkt an Druckerei übergeben — keine Anpassungen nötig. Bei Flyeralarm/Saxoprint/WIRmachenDRUCK einfach „Visitenkarte 85×55 mm hochkant/quer" wählen und PNG hochladen. Beschnitt + Sicherheitsabstand sind bereits eingerechnet.`}
            />
          </TabsContent>

          {/* ========= FLYER ========= */}
          <TabsContent value="flyer" className="space-y-6">
            <SectionHeader
              icon={Newspaper}
              title="Flyer · DIN A6"
              description="Hochformat 105 × 148 mm — perfekt zum Auslegen in Werkstätten, Kanzleien oder beim ersten Kundenkontakt. Daten werden aus deinem Profil übernommen."
            />

            <PrintFieldsCard
              name={name} setName={setName}
              phone={sigPhone} setPhone={setSigPhone}
              email={sigEmail} setEmail={setSigEmail}
              web={printWeb} setWeb={setPrintWeb}
              showRole={false}
            />

            <div className="grid grid-cols-1 gap-4" data-testid="flyer-grid">
              {FLYER_TEMPLATES.map((t) => (
                <PrintTemplateCard
                  key={t.id}
                  template={t}
                  data={{ name, phone: sigPhone, email: sigEmail, web: printWeb }}
                  filenamePrefix={t.id}
                  hint="DIN A6 · 105 × 148 mm · 300 DPI"
                  large
                />
              ))}
            </div>

            <PrintHintCard
              format="DIN A6 · 105 × 148 mm (Hochformat)"
              dpi="300 DPI (1240 × 1748 px)"
              tip="Für mehr Reichweite: 250 Stück Flyer kosten online ab ~25 € — gib die PNG-Datei beim Druckdienstleister ab und wähle 135–170 g/m² Bilderdruck matt."
            />
          </TabsContent>

          {/* ========= DIN-LANG WICKELFALZ FLYER ========= */}
          <TabsContent value="dinlang" className="space-y-6">
            <SectionHeader
              icon={BookOpen}
              title="Falt-Flyer · DIN-lang Wickelfalz"
              description="Querformat 297 × 210 mm — wird auf 99 × 210 mm in 3 Panele gefaltet. Klassisches Format für Auslagen, Werkstätten und Briefkasten-Verteilung. Vor- und Innenseite separat als druckfertige PNGs."
            />

            <PrintFieldsCard
              name={name} setName={setName}
              phone={sigPhone} setPhone={setSigPhone}
              email={sigEmail} setEmail={setSigEmail}
              web={printWeb} setWeb={setPrintWeb}
              address={sigAddress} setAddress={setSigAddress}
              showRole={false}
            />

            <div className="grid grid-cols-1 gap-4" data-testid="dinlang-grid">
              {DINLANG_TEMPLATES.map((t) => (
                <PrintTemplateCard
                  key={t.id}
                  template={t}
                  data={{ name, phone: sigPhone, email: sigEmail, web: printWeb, address: sigAddress }}
                  filenamePrefix={t.id}
                  hint="DIN-lang Querformat · 297 × 210 mm · 300 DPI · 3 Panele à 99 mm"
                  large
                />
              ))}
            </div>

            <PrintHintCard
              format="DIN-lang Wickelfalz · 297 × 210 mm gefaltet auf 99 × 210 mm"
              dpi="300 DPI (3508 × 2480 px) je Seite"
              tip={`Beim Druckdienstleister „Wickelfalz 6-seitig" oder „Z-Falz" auswählen — die gestrichelten Hilfslinien zeigen die Falzkante. Empfehlung: 135–170 g/m² Bilderdruck matt, 3 mm Beschnitt rundum.`}
            />
          </TabsContent>

          {/* ========= TÜRSCHILD ========= */}
          <TabsContent value="tuerschild" className="space-y-6">
            <SectionHeader
              icon={DoorOpen}
              title="Türschild / Praxisschild"
              description="DIN A4 Hochformat (210 × 297 mm) — perfekt für Bürotür, Werkstatt-Eingang oder Empfangstresen. Mit Öffnungszeiten und Kontaktblock."
            />

            <Card className="p-5" data-testid="tuerschild-fields">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name" value={name} onChange={setName} testid="ts-name" />
                <Field label="Rolle / Titel" value={printRole} onChange={setPrintRole} testid="ts-role" />
                <Field label="Telefon" value={sigPhone} onChange={setSigPhone} testid="ts-phone" />
                <Field label="Mobil" value={printMobile} onChange={setPrintMobile} testid="ts-mobile" />
                <Field label="E-Mail" type="email" value={sigEmail} onChange={setSigEmail} testid="ts-email" />
                <Field label="Web" value={printWeb} onChange={setPrintWeb} testid="ts-web" />
                <div className="sm:col-span-2">
                  <Field label="Adresse" value={sigAddress} onChange={setSigAddress} testid="ts-address" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Öffnungszeiten (eine Zeile pro Tag, max. 4 Zeilen)</Label>
                  <textarea
                    className="mt-1.5 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
                    rows={4}
                    value={printHours}
                    onChange={(e) => setPrintHours(e.target.value)}
                    data-testid="ts-hours"
                  />
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tuerschild-grid">
              {TUERSCHILD_TEMPLATES.map((t) => (
                <PrintTemplateCard
                  key={t.id}
                  template={t}
                  data={{
                    name, role: printRole,
                    phone: sigPhone, mobile: printMobile,
                    email: sigEmail, web: printWeb,
                    address: sigAddress, hours: printHours,
                  }}
                  filenamePrefix={t.id}
                  hint="DIN A4 · 210 × 297 mm · 300 DPI"
                />
              ))}
            </div>

            <PrintHintCard
              format="DIN A4 Hochformat · 210 × 297 mm"
              dpi="300 DPI (2480 × 3508 px)"
              tip="Tipp: 250 g/m² Karton oder hinter laminierter Folie + Magnet-Halterung — hält jahrelang an Bürotür oder Werkstatt-Empfang. Auch als Aufsteller ab ~3 € beim Druckdienstleister erhältlich."
            />
          </TabsContent>

          {/* ========= SOCIAL MEDIA TEMPLATES ========= */}
          <TabsContent value="social" className="space-y-6">
            <SectionHeader icon={Instagram} title="Social-Media-Vorlagen" description="Trage deinen Namen, Headline & Untertitel ein — die Vorschau aktualisiert sich live. Klick auf »PNG herunterladen«." />
            <Card className="p-5" data-testid="social-form">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Dein Name</Label>
                  <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="social-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Headline</Label>
                  <Input className="mt-1.5" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="social-headline" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Untertitel</Label>
                  <Input className="mt-1.5" value={sub} onChange={(e) => setSub(e.target.value)} data-testid="social-sub" />
                </div>
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="social-grid">
              {TEMPLATES.map((t) => (
                <SocialTemplate
                  key={t.id}
                  template={t}
                  name={name}
                  headline={headline}
                  sub={sub}
                />
              ))}
            </div>
          </TabsContent>

          {/* ========= EMAIL SIGNATURE ========= */}
          <TabsContent value="email" className="space-y-6">
            <SectionHeader icon={Mail} title="E-Mail-Signatur" description="Anpassen, Vorschau prüfen, HTML oder Plain-Text kopieren — und in Outlook, Gmail oder Thunderbird einfügen." />
            <Card className="p-5">
              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Name</Label>
                  <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="sig-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">E-Mail</Label>
                  <Input className="mt-1.5" type="email" value={sigEmail} onChange={(e) => setSigEmail(e.target.value)} data-testid="sig-email" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Telefon</Label>
                  <Input className="mt-1.5" value={sigPhone} onChange={(e) => setSigPhone(e.target.value)} data-testid="sig-phone" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Adresse</Label>
                  <Input className="mt-1.5" value={sigAddress} onChange={(e) => setSigAddress(e.target.value)} data-testid="sig-address" />
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">Vorschau</div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-5 overflow-x-auto">
                <div
                  data-testid="sig-preview"
                  dangerouslySetInnerHTML={{
                    __html: EMAIL_SIGNATURE_HTML({
                      name, role: "Kfz-Sachverständiger",
                      phone: sigPhone, email: sigEmail, address: sigAddress,
                    }),
                  }}
                />
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                <Button
                  onClick={() => copyToClipboard(
                    EMAIL_SIGNATURE_HTML({ name, role: "Kfz-Sachverständiger", phone: sigPhone, email: sigEmail, address: sigAddress }),
                    "HTML-Signatur kopiert"
                  )}
                  data-testid="sig-copy-html"
                >
                  <Copy className="h-4 w-4 mr-2" /> HTML kopieren
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(
                    EMAIL_SIGNATURE_TEXT({ name, role: "Kfz-Sachverständiger", phone: sigPhone, email: sigEmail, address: sigAddress }),
                    "Plain-Text-Signatur kopiert"
                  )}
                  data-testid="sig-copy-text"
                >
                  <Copy className="h-4 w-4 mr-2" /> Plain Text kopieren
                </Button>
              </div>

              <div className="mt-5 rounded-xl bg-cyan-50 border border-cyan-100 p-4 text-sm text-cyan-900">
                <div className="font-semibold mb-1">So fügst du die Signatur ein:</div>
                <ul className="list-disc pl-5 space-y-0.5 text-cyan-800/90">
                  <li><strong>Outlook</strong> → Datei → Optionen → E-Mail → Signaturen → Neu → HTML einfügen</li>
                  <li><strong>Gmail</strong> → Einstellungen → Allgemein → Signatur → HTML einfügen</li>
                  <li><strong>Thunderbird</strong> → Konten-Einstellungen → HTML im Signatur-Feld aktivieren</li>
                </ul>
              </div>
            </Card>
          </TabsContent>

          {/* ========= TEXTS ========= */}
          <TabsContent value="texts" className="space-y-4">
            <SectionHeader icon={FileText} title="Marketing-Texte" description="Vorgeschriebene Texte für Bio, Webseite, Mailings und Google-Profile — dein Name wird automatisch eingesetzt." />
            <Card className="p-3 mb-4">
              <div className="flex items-center gap-3 px-2">
                <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 shrink-0">Dein Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1" data-testid="texts-name" />
              </div>
            </Card>
            <div className="space-y-3" data-testid="texts-list">
              {MARKETING_TEXTS.map((t) => (
                <MarketingTextCard key={t.id} text={t.text(name)} title={t.title} description={t.description} testid={`text-${t.id}`} />
              ))}
            </div>
          </TabsContent>

          {/* ========= WEB BADGES ========= */}
          <TabsContent value="web" className="space-y-6">
            <SectionHeader icon={Globe} title="Web-Badges & Embed" description="Zeig deinen Kunden, dass du Easy Gutachter Partner bist — direkt auf deiner Webseite." />
            <div className="grid sm:grid-cols-2 gap-4">
              <BadgeCard
                preview={
                  <a href="#" className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow">
                    <img src="/easy-gutachter-logo.png" alt="" className="h-5 w-5" />
                    Easy Gutachter Partner
                  </a>
                }
                title="Partner-Badge (klein)"
                html={`<a href="https://easy-gutachter.de" target="_blank" rel="noreferrer" style="display:inline-flex;align-items:center;gap:.5rem;background:linear-gradient(90deg,#06B6D4,#10B981);color:#fff;padding:.5rem 1rem;border-radius:9999px;font-family:Arial,sans-serif;font-weight:600;font-size:14px;text-decoration:none">
  <img src="https://easy-gutachter.de/logo.png" alt="" style="height:20px;width:20px"/>
  Easy Gutachter Partner
</a>`}
                testid="web-badge-small"
              />

              <BadgeCard
                preview={
                  <div className="inline-flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3 shadow-sm">
                    <img src="/easy-gutachter-logo.png" alt="" className="h-10 w-10" />
                    <div>
                      <div className="font-display font-bold text-sm">Easy Gutachter Partner</div>
                      <div className="text-[11px] text-zinc-500">Digitale Kfz-Gutachten</div>
                    </div>
                  </div>
                }
                title="Vertrauens-Banner"
                html={`<a href="https://easy-gutachter.de" target="_blank" rel="noreferrer" style="display:inline-flex;align-items:center;gap:.75rem;background:#fff;border:1px solid #e4e4e7;border-radius:.75rem;padding:.75rem 1rem;text-decoration:none;font-family:Arial,sans-serif">
  <img src="https://easy-gutachter.de/logo.png" alt="" style="height:40px;width:40px"/>
  <span>
    <span style="display:block;font-weight:700;color:#0F172A;font-size:14px">Easy Gutachter Partner</span>
    <span style="display:block;color:#6b7280;font-size:11px">Digitale Kfz-Gutachten</span>
  </span>
</a>`}
                testid="web-badge-trust"
              />
            </div>
          </TabsContent>

          {/* ========= SCHULUNG ========= */}
          <TabsContent value="schulung" className="space-y-6">
            <SectionHeader
              icon={GraduationCap}
              title="Schulungs-Präsentation"
              description="Fertige PowerPoint für die Software-Schulung — 24 Folien, komplett im Easy-Gutachter-Design."
            />
            <Card className="p-6 max-w-3xl" data-testid="training-pptx-card">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-sky-500 to-violet-600 text-white flex items-center justify-center shrink-0">
                  <GraduationCap className="h-7 w-7" />
                </div>
                <div className="flex-1 min-w-[260px]">
                  <div className="font-display font-semibold text-lg tracking-tight">Easy Gutachter — Software-Schulung (PPTX)</div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Komplette Schulung in 25 Folien — <b>mit echten App-Screenshots und Abo-Modellen/Preisen</b>:
                    Rollen & Login, Akte anlegen, Fotos & KI-Funktionen, Kalkulation & Gutachten,
                    Kunden-Magic-Link, Provisionspartner, Buchhaltung mit Stripe-SEPA-Einzug, Mahnwesen,
                    Shop & Best Practices — inkl. Praxis-Tipps auf jeder Folie.
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-zinc-400">
                    <Badge variant="outline" className="text-[10px]">25 Folien</Badge>
                    <Badge variant="outline" className="text-[10px]">13 App-Screenshots</Badge>
                    <Badge variant="outline" className="text-[10px]">Abo-Modelle & Preise</Badge>
                    <Badge variant="outline" className="text-[10px]">PPTX + PDF</Badge>
                    <Badge variant="outline" className="text-[10px]">Deutsch</Badge>
                  </div>
                  <Button className="mt-4" onClick={downloadTrainingPptx} disabled={pptxBusy} data-testid="training-pptx-download-btn">
                    {pptxBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    PowerPoint herunterladen
                  </Button>
                  <Button variant="outline" className="mt-4 ml-2" onClick={downloadTrainingPdf} disabled={pdfBusy} data-testid="training-pdf-download-btn">
                    {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                    PDF-Handout herunterladen
                  </Button>
                  <div className="text-xs text-zinc-400 mt-3">
                    Tipp: Neue Nutzer bekommen beim ersten Login automatisch eine geführte Tour durch die App
                    (jederzeit neu startbar über Profil-Menü → „Tour starten").
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

/* ================ Sub-components ================ */

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-white" strokeWidth={1.8} />
      </div>
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        <p className="text-sm text-zinc-500 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function LogoCard({ children, bg, label, hint, onDownload, onDownloadPng, pngLabel, testid }) {
  return (
    <Card className="overflow-hidden flex flex-col" data-testid={testid}>
      <div className={cn("aspect-square flex items-center justify-center p-6", bg)}>
        {children}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display font-semibold tracking-tight">{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5 flex-1">{hint}</div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" onClick={onDownload}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download
          </Button>
          {onDownloadPng && (
            <Button size="sm" variant="outline" onClick={onDownloadPng}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> {pngLabel || "PNG"}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function FontCard({ role, family, preview, sample, href, style }) {
  return (
    <Card className="p-5" data-testid={`font-${family.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">{role}</div>
      <div className="font-display text-3xl tracking-tight" style={style}>{preview}</div>
      <div className="text-base text-zinc-500 mt-1" style={style}>{sample}</div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
        <div className="text-sm font-mono">{family}</div>
        <a href={href} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1">
          <Download className="h-3.5 w-3.5" /> Quelle
        </a>
      </div>
    </Card>
  );
}

function SocialTemplate({ template, name, headline, sub }) {
  const svg = useMemo(() => template.build(name, headline, sub), [template, name, headline, sub]);
  const [downloading, setDownloading] = useState(false);

  const onDownload = async () => {
    setDownloading(true);
    try {
      await svgStringToPngDownload(svg, `${template.id}.png`, { width: template.w, height: template.h });
    } catch (e) {
      toast.error("PNG-Export fehlgeschlagen");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="overflow-hidden flex flex-col group hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`tmpl-${template.id}`}>
      <div className="relative bg-zinc-100" style={{ aspectRatio: template.w / template.h }}>
        <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svg }} style={{ display: "flex", alignItems: "stretch" }} />
        <Badge className="absolute top-3 left-3 bg-white/95 text-zinc-700 hover:bg-white text-[10px] font-bold uppercase tracking-wider z-10">
          {template.format}
        </Badge>
      </div>
      <div className="p-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm truncate">{template.label}</div>
          <div className="text-[11px] text-zinc-500">{template.w} × {template.h} px</div>
        </div>
        <Button size="sm" onClick={onDownload} disabled={downloading} data-testid={`tmpl-${template.id}-download`}>
          {downloading ? <Sparkles className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          PNG
        </Button>
      </div>
    </Card>
  );
}

function MarketingTextCard({ title, description, text, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await copyToClipboard(text, "Text kopiert");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Card className="p-5 hover:border-emerald-300 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-display font-semibold tracking-tight">{title}</div>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
        </div>
        <Button size="sm" variant="outline" onClick={onCopy} className="shrink-0" data-testid={`${testid}-copy`}>
          {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
          {copied ? "Kopiert" : "Kopieren"}
        </Button>
      </div>
      <pre className="rounded-lg bg-zinc-50 border border-zinc-100 p-4 text-[13px] text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-y-auto">{text}</pre>
    </Card>
  );
}

function BadgeCard({ preview, title, html, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">{title}</div>
      <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-6 flex items-center justify-center">
        {preview}
      </div>
      <pre className="mt-3 rounded-lg bg-zinc-900 text-zinc-100 p-3 text-[11px] font-mono whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{html}</pre>
      <Button size="sm" variant="outline" onClick={() => copyToClipboard(html, "HTML-Code kopiert")} className="mt-3" data-testid={`${testid}-copy`}>
        <Copy className="h-3.5 w-3.5 mr-1.5" /> Embed-Code kopieren
      </Button>
    </Card>
  );
}

function PrintFieldsCard({
  name, setName,
  role, setRole,
  phone, setPhone,
  mobile, setMobile,
  email, setEmail,
  web, setWeb,
  address, setAddress,
  showRole = true,
}) {
  return (
    <Card className="p-5" data-testid="print-fields">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Name" value={name} onChange={setName} testid="print-name" />
        {showRole && (
          <Field label="Rolle / Titel" value={role} onChange={setRole} testid="print-role" />
        )}
        <Field label="Telefon" value={phone} onChange={setPhone} testid="print-phone" />
        {setMobile !== undefined && (
          <Field label="Mobil" value={mobile} onChange={setMobile} testid="print-mobile" />
        )}
        <Field label="E-Mail" type="email" value={email} onChange={setEmail} testid="print-email" />
        <Field label="Web" value={web} onChange={setWeb} testid="print-web" />
        {setAddress !== undefined && (
          <div className="sm:col-span-2">
            <Field label="Adresse" value={address} onChange={setAddress} testid="print-address" />
          </div>
        )}
      </div>
    </Card>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
      <Input
        className="mt-1.5"
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function PrintTemplateCard({ template, data, filenamePrefix, hint, large = false }) {
  const svg = useMemo(() => template.build(data), [template, data]);
  const [busy, setBusy] = useState(false);

  const onPng = async () => {
    setBusy(true);
    try {
      await svgStringToPngDownload(svg, `${filenamePrefix}.png`, { width: template.w, height: template.h });
      toast.success("PNG heruntergeladen");
    } catch {
      toast.error("PNG-Export fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const onSvg = () => {
    downloadSvgString(svg, `${filenamePrefix}.svg`);
    toast.success("SVG heruntergeladen");
  };

  return (
    <Card className="overflow-hidden flex flex-col group hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`tmpl-${template.id}`}>
      <div className={cn("relative bg-zinc-100", large && "p-4")} style={{ aspectRatio: template.w / template.h }}>
        <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svg }} style={{ display: "flex", alignItems: "stretch" }} />
        <Badge className="absolute top-3 left-3 bg-white/95 text-zinc-700 hover:bg-white text-[10px] font-bold uppercase tracking-wider z-10 shadow-sm">
          {template.label}
        </Badge>
      </div>
      <div className="p-3 flex items-center gap-2 border-t border-zinc-100">
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm truncate">{template.label}</div>
          <div className="text-[11px] text-zinc-500">{hint}</div>
        </div>
        <Button size="sm" variant="outline" onClick={onSvg} data-testid={`tmpl-${template.id}-svg`}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> SVG
        </Button>
        <Button size="sm" onClick={onPng} disabled={busy} data-testid={`tmpl-${template.id}-png`}>
          {busy ? <Sparkles className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          PNG
        </Button>
      </div>
    </Card>
  );
}

function EasyGutachterEmailField({ value, onChange, testid }) {
  const sanitize = (v) => String(v || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9.\-_]/g, "");

  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">E-Mail</Label>
      <div className="mt-1.5 flex items-stretch rounded-md border border-zinc-200 bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500">
        <input
          type="text"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-transparent"
          value={value || ""}
          onChange={(e) => onChange(sanitize(e.target.value))}
          placeholder="vorname.nachname"
          data-testid={testid}
        />
        <div className="flex items-center px-3 bg-zinc-100 text-sm text-zinc-600 select-none border-l border-zinc-200" data-testid={`${testid}-suffix`}>
          @easygutachter.de
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-1.5">
        Die Domain bleibt fix — du kannst nur den Namen-Teil ändern.
      </div>
    </div>
  );
}

function EasyGutachterWebField({ value, onChange, testid }) {
  const sanitize = (v) => String(v || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-");

  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">Web-Adresse</Label>
      <div className="mt-1.5 flex items-stretch rounded-md border border-zinc-200 bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500">
        <div className="flex items-center px-3 bg-zinc-100 text-sm text-zinc-600 select-none border-r border-zinc-200 whitespace-nowrap" data-testid={`${testid}-prefix`}>
          www.easygutachter.de/
        </div>
        <input
          type="text"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-transparent"
          value={value || ""}
          onChange={(e) => onChange(sanitize(e.target.value))}
          placeholder="vorname-nachname"
          data-testid={testid}
        />
      </div>
      <div className="text-[11px] text-zinc-500 mt-1.5 flex items-center gap-2 flex-wrap">
        <span>Direkt-Link zu deiner Easy-Gutachter-Profilseite — nur der Name-Teil ist editierbar.</span>
        {value && (
          <a
            href={`/partner/${value}`}
            target="_blank"
            rel="noreferrer"
            className="text-cyan-700 hover:underline font-medium inline-flex items-center gap-1"
            data-testid={`${testid}-preview-link`}
          >
            Vorschau öffnen ↗
          </a>
        )}
      </div>
    </div>
  );
}

function PrintHintCard({ format, dpi, tip }) {
  return (
    <Card className="p-5 bg-cyan-50 border-cyan-100" data-testid="print-hint">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-cyan-500 flex items-center justify-center shrink-0">
          <Printer className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 text-sm text-cyan-900">
          <div className="font-display font-bold tracking-tight mb-1 flex items-center gap-2">
            <Info className="h-3.5 w-3.5" /> Druckhinweis
          </div>
          <div className="space-y-0.5 text-cyan-900/90">
            <div><strong>Format:</strong> {format}</div>
            <div><strong>Auflösung:</strong> {dpi}</div>
            <div className="pt-1 text-cyan-800/90">{tip}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}
